type TaskFieldOption = {
  id: string;
  label: string;
};

export type ExecutorSource = 'form' | 'location_responsible' | 'location_from_question' | 'from_question';
export type ValidatorSource = 'form' | 'from_question';

export type IsTaskFieldConfig = {
  key: string;
  label: string;
  responseType: string;
  required?: boolean;
  isMultiple?: boolean;
  defaultValue?: any;
  options?: TaskFieldOption[];
  taskTarget?: string;
  executorSource?: ExecutorSource;
  /** Key tipo "subsectionIndex_nPregunta" para buscar la ubicación en answersIndex. Solo aplica cuando executorSource es location_from_question. */
  executorLocationFieldKey?: string;
  /** Key tipo "subsectionIndex_nPregunta" para usuarios. Solo aplica cuando executorSource es from_question. */
  executorQuestionFieldKey?: string;
  validatorSource?: ValidatorSource;
  /** Key tipo "subsectionIndex_nPregunta" para usuarios. Solo aplica cuando validatorSource es from_question. */
  validatorQuestionFieldKey?: string;
};

export type IsTaskConfig = {
  enabled: boolean;
  fields: IsTaskFieldConfig[];
  mapping?: Record<string, string>;
  defaults?: Record<string, any>;
};

const LEGACY_FIELDS: IsTaskFieldConfig[] = [
  {
    key: 'titulo',
    label: 'Tarea',
    responseType: 'textField',
    required: true,
    taskTarget: 'titulo',
  },
  {
    key: 'ejecutor',
    label: 'Ejecutor',
    responseType: 'usuario',
    required: true,
    taskTarget: 'ejecutor',
  },
  // {
  //   key: 'inicio',
  //   label: 'Inicio',
  //   responseType: 'date',
  //   required: false,
  //   taskTarget: 'inicio',
  // },
  // {
  //   key: 'fechaLimite',
  //   label: 'Fin',
  //   responseType: 'date',
  //   required: false,
  //   taskTarget: 'fechaLimite',
  // },
];

export const IS_TASK_RESPONSE_TYPE_OPTIONS = [
  {value: 'textField', label: 'Texto'},
  {value: 'numberField', label: 'Numero'},
  {value: 'multipleChoice', label: 'Lista desplegable'},
  {value: 'multiCheckbox', label: 'Lista checkbox'},
  {value: 'checkbox', label: 'Checkbox'},
  {value: 'date', label: 'Fecha'},
  {value: 'time', label: 'Hora'},
  {value: 'usuario', label: 'Usuario'},
  {value: 'perfilEmpleado', label: 'Colaborador'},
  {value: 'location', label: 'Ubicacion'},
  {value: 'thirdParty', label: 'Tercero'},
  {value: 'asset', label: 'Activo'},
  {value: 'planificacion', label: 'Planificacion'},
  {value: 'project', label: 'Proyecto'},
  {value: 'modeloTarea', label: 'Modelo de tarea'},
  {value: 'ammount', label: 'Monto'},
];

/** Orden alineado con TaskCreateStep1 / envío normal de tareas */
export const IS_TASK_TARGET_OPTIONS = [
  {value: 'modeloTarea', label: 'Modelo de tarea'},
  {value: 'titulo', label: 'Título tarea'},
  {value: 'prioridad', label: 'Prioridad'},
  {value: 'tercero', label: 'Tercero'},
  {value: 'planificacion', label: 'Planificación'},
  {value: 'activos', label: 'Activos'},
  {value: 'ejecutor', label: 'Ejecutor'},
  {value: 'validadores', label: 'Validadores'},
  {value: 'inicio', label: 'Inicio'},
  {value: 'fechaLimite', label: 'Fecha límite'},
];

export const DEFAULT_LABEL_BY_TARGET: Record<string, string> = {
  modeloTarea: 'Modelo de tarea',
  titulo: 'Título',
  descripcion: 'Descripción',
  ejecutor: 'Ejecutor',
  validadores: 'Validadores',
  planificacion: 'Planificación',
  activos: 'Activos',
  tercero: 'Tercero',
  inicio: 'Fecha inicio',
  fechaLimite: 'Fecha límite',
  prioridad: 'Prioridad',
  comentarioEnviado: 'Comentario enviado',
};

export const EXECUTOR_SOURCE_OPTIONS: {value: ExecutorSource; label: string}[] = [
  {value: 'form', label: 'Selección en el formulario'},
  {value: 'location_responsible', label: 'Responsable de la ubicación de la tarea'},
  {value: 'location_from_question', label: 'Responsable de la ubicación desde pregunta del formulario'},
  {value: 'from_question', label: 'Desde pregunta'},
];

export const VALIDATOR_SOURCE_OPTIONS: {value: ValidatorSource; label: string}[] = [
  {value: 'form', label: 'Selección en el formulario'},
  {value: 'from_question', label: 'Desde pregunta'},
];

export const isIsTaskFieldHiddenFromSource = (field: {
  taskTarget?: string;
  executorSource?: string;
  validatorSource?: string;
}): boolean => {
  if (field?.taskTarget === 'ejecutor') {
    return (
      field.executorSource === 'location_responsible' ||
      field.executorSource === 'location_from_question' ||
      field.executorSource === 'from_question'
    );
  }
  if (field?.taskTarget === 'validadores') {
    return field.validatorSource === 'from_question';
  }
  return false;
};

export const MULTIPLE_BY_TARGET = new Set(['ejecutor', 'validadores', 'activos', 'tercero']);
const FORCED_RESPONSE_TYPE_BY_TARGET: Record<string, string> = {
  modeloTarea: 'modeloTarea',
  planificacion: 'planificacion',
  ejecutor: 'usuario',
  validadores: 'usuario',
  activos: 'asset',
  tercero: 'thirdParty',
  inicio: 'date',
  fechaLimite: 'date',
  prioridad: 'multipleChoice',
};

/** Destinos sin tipo único fijo: solo se conserva responseType si es coherente (evita p. ej. titulo + usuario por datos viejos o merges). */
const FLEXIBLE_RESPONSE_TYPES_BY_TARGET: Record<string, string[]> = {
  titulo: ['textField', 'numberField'],
  descripcion: ['textField', 'numberField'],
  comentarioEnviado: ['textField', 'numberField'],
};

const normalizeTaskTarget = (target: any) => {
  const normalized = cleanText(target).toLowerCase();
  if (!normalized) return 'titulo';

  if (['modelotarea', 'modelo_tarea', 'task', 'tarea'].includes(normalized)) return 'modeloTarea';
  if (['ejecutores', 'executors', 'executor'].includes(normalized)) return 'ejecutor';
  if (['validator', 'validators', 'validador'].includes(normalized)) return 'validadores';
  if (['planning'].includes(normalized)) return 'planificacion';
  if (['assets', 'asset', 'activo', 'activos'].includes(normalized)) return 'activos';
  if (['thirdparty', 'third_party'].includes(normalized)) return 'tercero';
  if (['tasksubtitle', 'subtitulo'].includes(normalized)) return 'descripcion';
  if (['datestart', 'inicio', 'fecha_inicio'].includes(normalized)) return 'inicio';
  if (['dateend', 'fechalimite', 'fecha_limite', 'fin'].includes(normalized)) return 'fechaLimite';
  if (['priority'].includes(normalized)) return 'prioridad';

  return normalized;
};

export const inferResponseType = (target: string, fallback: string = '') => {
  const normalizedTarget = normalizeTaskTarget(target);
  const forcedType = FORCED_RESPONSE_TYPE_BY_TARGET[normalizedTarget];
  if (forcedType) return forcedType;
  const flexibleAllowed = FLEXIBLE_RESPONSE_TYPES_BY_TARGET[normalizedTarget];
  if (flexibleAllowed) {
    const fb = String(fallback ?? '').trim();
    if (fb && flexibleAllowed.includes(fb)) return fb;
    return flexibleAllowed[0];
  }
  if (fallback) return fallback;
  return 'textField';
};

const cleanText = (value: any) =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ');

const toKey = (value: any, fallback: string) => {
  const normalized = cleanText(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return normalized || fallback;
};

const normalizeOptions = (raw: any[] = []): TaskFieldOption[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((option, index) => {
      const label = cleanText(option?.label ?? option);
      if (!label) return null;
      const id = String(option?.id ?? toKey(label, `opt_${index + 1}`));
      return {id, label};
    })
    .filter(Boolean) as TaskFieldOption[];
};

const inferTarget = (field: any) => {
  const label = cleanText(field?.label ?? field?.titulo ?? field?.pregunta?.titulo).toLowerCase();
  const key = cleanText(field?.key ?? field?.taskTarget ?? field?.target).toLowerCase();
  const haystack = `${label} ${key}`;

  if (haystack.includes('modelo') && haystack.includes('tarea')) return 'modeloTarea';
  if (haystack.includes('ejecutor') || haystack.includes('usuario')) return 'ejecutor';
  if (haystack.includes('validador')) return 'validadores';
  if (haystack.includes('planificacion')) return 'planificacion';
  if (haystack.includes('activo')) return 'activos';
  if (haystack.includes('tercero')) return 'tercero';
  if (haystack.includes('inicio')) return 'inicio';
  if (haystack.includes('fin') || haystack.includes('limite')) return 'fechaLimite';
  if (haystack.includes('descripcion')) return 'descripcion';
  if (haystack.includes('prioridad')) return 'prioridad';
  if (haystack.includes('comentario')) return 'comentarioEnviado';
  return 'titulo';
};

const normalizeField = (rawField: any, index: number): IsTaskFieldConfig => {
  const taskTarget = normalizeTaskTarget(rawField?.taskTarget ?? rawField?.target ?? inferTarget(rawField));
  const defaultLabel = DEFAULT_LABEL_BY_TARGET[taskTarget] ?? `Campo ${index + 1}`;
  const label = cleanText(rawField?.label ?? rawField?.titulo ?? rawField?.pregunta?.titulo ?? defaultLabel);
  const key = String(rawField?.key ?? taskTarget ?? toKey(label, `campo_${index + 1}`));
  const responseType = inferResponseType(taskTarget, String(rawField?.responseType ?? rawField?.tipoRespuesta ?? ''));

  const result: IsTaskFieldConfig = {
    key,
    label,
    responseType,
    required: Boolean(rawField?.required ?? rawField?.isRequired ?? false),
    isMultiple: Boolean(rawField?.isMultiple ?? MULTIPLE_BY_TARGET.has(taskTarget)),
    defaultValue: rawField?.defaultValue,
    options: normalizeOptions(rawField?.options ?? rawField?.alternativas),
    taskTarget,
  };
  if (taskTarget === 'ejecutor' && rawField?.executorSource) {
    result.executorSource = rawField.executorSource as ExecutorSource;
    if (result.executorSource === 'location_from_question' && rawField?.executorLocationFieldKey) {
      result.executorLocationFieldKey = String(rawField.executorLocationFieldKey).trim() || undefined;
    }
    if (result.executorSource === 'from_question' && rawField?.executorQuestionFieldKey) {
      result.executorQuestionFieldKey = String(rawField.executorQuestionFieldKey).trim() || undefined;
    }
  }
  if (taskTarget === 'validadores' && rawField?.validatorSource) {
    result.validatorSource = rawField.validatorSource as ValidatorSource;
    if (result.validatorSource === 'from_question' && rawField?.validatorQuestionFieldKey) {
      result.validatorQuestionFieldKey = String(rawField.validatorQuestionFieldKey).trim() || undefined;
    }
  }
  return result;
};

export const buildDefaultIsTaskConfig = (): IsTaskConfig => ({
  enabled: true,
  fields: LEGACY_FIELDS.map((field) => ({...field})),
  mapping: {},
  defaults: {},
});

export const normalizeIsTaskConfig = (subsection: any): IsTaskConfig | null => {
  if (!subsection?.isTask) return null;

  const rawConfig = subsection?.taskConfig ?? {};
  const hasExplicitFields = rawConfig?.fields && Array.isArray(rawConfig.fields);
  const baseFields = hasExplicitFields
    ? rawConfig.fields
    : Array.isArray(subsection?.preguntas) && subsection.preguntas.length
      ? subsection.preguntas
      : LEGACY_FIELDS;

  const fields = baseFields.map(normalizeField);
  const mapping = rawConfig?.mapping && typeof rawConfig.mapping === 'object' ? {...rawConfig.mapping} : {};
  const defaults = rawConfig?.defaults && typeof rawConfig.defaults === 'object' ? {...rawConfig.defaults} : {};

  fields.forEach((field) => {
    if (!mapping[field.key] && field.taskTarget) mapping[field.key] = field.taskTarget;
    if (field.defaultValue !== undefined && defaults[field.key] === undefined) defaults[field.key] = field.defaultValue;
  });

  return {
    enabled: rawConfig?.enabled !== false,
    fields,
    mapping,
    defaults,
  };
};

const buildOptionsForQuestion = (options: TaskFieldOption[]) => {
  return options.map((option) => ({id: option.id, label: option.label}));
};

const buildDefaultValueForQuestion = (field: IsTaskFieldConfig) => {
  if (field.defaultValue === undefined || field.defaultValue === null || field.defaultValue === '') return undefined;
  if (field.responseType === 'multiCheckbox' && Array.isArray(field.options)) {
    const selected = Array.isArray(field.defaultValue) ? field.defaultValue.map(String) : [String(field.defaultValue)];
    return field.options.map((option) => ({
      id: option.id,
      label: option.label,
      value: selected.includes(String(option.id)),
    }));
  }
  return field.defaultValue;
};

const DEFAULT_OPTIONS_BY_TARGET: Record<string, TaskFieldOption[]> = {
  prioridad: [
    {id: 'Baja', label: 'Baja'},
    {id: 'Media', label: 'Media'},
    {id: 'Alta', label: 'Alta'},
  ],
};

export const buildIsTaskQuestionsFromConfig = (subsection: any) => {
  const config = normalizeIsTaskConfig(subsection);
  if (!config?.fields?.length) return [];

  return config.fields.map((field, index) => {
    const isHiddenFromSource = isIsTaskFieldHiddenFromSource(field);

    const pregunta: any = {
      nPregunta: index + 1,
      pregunta: {
        titulo: field.label,
        label: field.label,
      },
      tipoRespuesta: field.responseType,
      keyPointer: `isTask_${field.key}`,
      isRequired: isHiddenFromSource ? false : Boolean(field.required),
      isTaskFieldKey: field.key,
      isTaskTaskTarget: field.taskTarget,
      isMultiple: Boolean(field.isMultiple),
      isTaskExecutorFromSource: isHiddenFromSource,
    };

    if (field.responseType === 'multipleChoice') {
      const options = field.options?.length ? field.options : DEFAULT_OPTIONS_BY_TARGET[field.taskTarget || field.key] || [];
      pregunta.alternativas = buildOptionsForQuestion(options);
    }

    if (field.responseType === 'multiCheckbox') {
      pregunta.alternativasCheckbox = buildOptionsForQuestion(field.options || []);
    }

    const defaultValue = buildDefaultValueForQuestion(field);
    if (defaultValue !== undefined && !isHiddenFromSource) {
      pregunta[field.responseType] = defaultValue;
    }

    return pregunta;
  });
};
